import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import BrandMark from "./BrandMark";

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const closeMobile = () => setMobileOpen(false);

  const isSnapshot = location.pathname.startsWith("/snapshot/");
  const isOpsPage = location.pathname.startsWith("/ai-operations")
                    || location.pathname === "/book-efficiency-review"
                    || location.pathname === "/buy-assessment";

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 w-full backdrop-blur-md bg-[#080807]/85 border-b border-[#2a2620]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link" className="group min-w-0 shrink">
          <BrandMark layout="horizontal" size={36} className="sm:!h-[44px] group-hover:opacity-90 transition-opacity" />
        </Link>

        {!isSnapshot && (
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#c9bca4]">
            <a href="/#how"        className="hover:text-[#b8963e] transition-colors" data-testid="nav-how">How it works</a>
            <a href="/#divisions"  className="hover:text-[#b8963e] transition-colors" data-testid="nav-visibility">AI Visibility</a>
            <Link to="/ai-operations" className="hover:text-[#b8963e] transition-colors" data-testid="nav-operations">AI Operations</Link>
            <Link to="/research"   className="hover:text-[#b8963e] transition-colors" data-testid="nav-research">Research</Link>
          </nav>
        )}

        <div className="flex items-center gap-2">
          {user && user !== false ? (
            <>
              {user.role === "admin" ? (
                <>
                  <Link
                    to="/dashboard"
                    data-testid="nav-dashboard"
                    className="hidden sm:inline-flex text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/pipeline"
                    data-testid="nav-pipeline"
                    className="hidden sm:inline-flex text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
                  >
                    Pipeline
                  </Link>
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className="hidden sm:inline-flex text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
                  >
                    Admin
                  </Link>
                  <Link
                    to="/admin/publications"
                    data-testid="nav-admin-publications"
                    className="hidden lg:inline-flex text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
                  >
                    Publications
                  </Link>
                  <Link
                    to="/audit/new"
                    data-testid="nav-cta-run"
                    className="btn-gold text-sm py-2"
                  >
                    Run Snapshot
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/my-snapshot"
                    data-testid="nav-my-snapshot"
                    className="hidden sm:inline-flex text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
                  >
                    My Snapshot
                  </Link>
                  <Link
                    to="/audit/new"
                    data-testid="nav-cta-run"
                    className="btn-gold text-sm py-2"
                  >
                    Run Snapshot
                  </Link>
                </>
              )}
              <button
                onClick={handleLogout}
                data-testid="nav-logout"
                className="text-sm text-[#8a7e6d] hover:text-[#f4efe6] px-3 py-2 transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login"
                className="text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
              >
                Sign in
              </Link>
              {isOpsPage ? (
                <>
                  <Link
                    to="/signup"
                    data-testid="nav-cta-secondary"
                    className="hidden lg:inline-flex text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
                  >
                    Run Snapshot
                  </Link>
                  <Link
                    to="/book-efficiency-review"
                    data-testid="nav-cta-primary"
                    className="btn-gold text-sm py-2"
                  >
                    Book Efficiency Review
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/book-efficiency-review"
                    data-testid="nav-cta-secondary"
                    className="hidden lg:inline-flex text-sm text-[#c9bca4] hover:text-[#b8963e] px-3 py-2 transition-colors"
                  >
                    Book Efficiency Review
                  </Link>
                  <Link
                    to="/signup"
                    data-testid="nav-cta-primary"
                    className="btn-gold text-sm py-2"
                  >
                    Run Snapshot
                  </Link>
                </>
              )}
            </>
          )}

          {/* Mobile menu trigger — visible only below md */}
          {!isSnapshot && (
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-expanded={mobileOpen}
              aria-label="Toggle navigation"
              data-testid="nav-mobile-toggle"
              className="md:hidden ml-1 p-2 -mr-2 text-[#c9bca4] hover:text-[#b8963e] transition-colors"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && !isSnapshot && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-[#2a2620] bg-[#080807]/95 backdrop-blur-md"
        >
          <nav className="px-6 py-5 flex flex-col gap-1 text-sm">
            <a href="/#how" onClick={closeMobile}
               className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]"
               data-testid="nav-mobile-how">How it works</a>
            <a href="/#divisions" onClick={closeMobile}
               className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]"
               data-testid="nav-mobile-visibility">AI Visibility</a>
            <Link to="/ai-operations" onClick={closeMobile}
                  className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]"
                  data-testid="nav-mobile-operations">AI Operations</Link>
            <Link to="/research" onClick={closeMobile}
                  data-testid="nav-mobile-research"
                  className="py-2.5 text-[#c9bca4] hover:text-[#b8963e] flex items-center justify-between">
              <span>The AI Visibility Index™</span>
              <span className="text-[#b8963e] font-mono-eav text-[10px] tracking-[0.2em] uppercase">Research</span>
            </Link>
            <div className="h-px bg-[#2a2620] my-2" />
            {user && user !== false ? (
              <>
                {user.role === "admin" ? (
                  <>
                    <Link to="/dashboard" onClick={closeMobile} className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]">Dashboard</Link>
                    <Link to="/pipeline"  onClick={closeMobile} className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]">Visibility Pipeline</Link>
                    <Link to="/admin/operations" onClick={closeMobile} className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]">Operations Pipeline</Link>
                    <Link to="/admin"     onClick={closeMobile} className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]">Admin</Link>
                    <Link to="/admin/publications" onClick={closeMobile} className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]">Publications CMS</Link>
                  </>
                ) : (
                  <Link to="/my-snapshot" onClick={closeMobile} className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]">My Snapshot</Link>
                )}
                <Link to="/audit/new" onClick={closeMobile} className="btn-gold text-sm py-2 mt-2 justify-center">Run Snapshot</Link>
                <button onClick={() => { closeMobile(); handleLogout(); }} className="py-2.5 text-left text-[#8a7e6d] hover:text-[#f4efe6]">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login"  onClick={closeMobile} className="py-2.5 text-[#c9bca4] hover:text-[#b8963e]">Sign in</Link>
                <Link to="/signup" onClick={closeMobile} className="btn-gold text-sm py-2 mt-2 justify-center">Run Snapshot</Link>
                <Link to="/book-efficiency-review" onClick={closeMobile} className="btn-ghost text-sm py-2 mt-2 justify-center">Book Efficiency Review</Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
